import type { HardwareListGroup, HardwareListItem, MaterialsNodeData, ProposalNodeSet } from '../types';
import { postBasesItemTotal } from './proposalStructurePostBases';

export interface ParsedHardwareCsvItem {
  storey: string;
  productionNumber: string;
  csvGroup: string;
  group: HardwareListGroup;
  subGroup: string;
  name: string;
  material: string;
  section: string;
  length: string;
  lengthRough: string;
  quantity: number;
  weightLb: number;
}

export interface HardwareCsvParseResult {
  items: ParsedHardwareCsvItem[];
  storeys: string[];
  errors: string[];
}

const HARDWARE_NAME_RE = /steel|hardware/i;

export const HARDWARE_GROUP_LABEL: Record<HardwareListGroup, string> = {
  steel: 'Steel',
  fasteners: 'Fasteners',
  pegs: 'Pegs',
  other: 'Other',
};

export const HARDWARE_TABLE_GROUPS: HardwareListGroup[] = ['pegs', 'steel', 'fasteners'];
export const HARDWARE_LUMP_GROUPS: Array<'pegs' | 'fasteners'> = ['pegs', 'fasteners'];

export function filenameLooksLikeHardwareList(name: string | undefined): boolean {
  return HARDWARE_NAME_RE.test(name || '');
}

/** Cadwork steel / hardware tally: Weight + X-section, or a STEEL group column. */
export function isCadworkSteelCsv(csvText: string): boolean {
  const lines = csvText.replace(/^\uFEFF/, '').split(/\r?\n/).slice(0, 16);
  const joined = lines.join('\n').toLowerCase();
  const hasWeight = /\bweight(\s+real)?\b/.test(joined);
  const hasSection = /x-section|xsection/.test(joined);
  if (hasWeight && hasSection) return true;
  return lines.some((line) => {
    const cells = parseCsvLine(line).map((c) => c.trim().toUpperCase());
    return cells.includes('STEEL') && (cells.includes('STRUCTURAL') || cells.includes('COLUMN') || cells.includes('PLATE'));
  });
}

export function csvListStreamKind(
  file: Pick<File, 'name' | 'type'>,
  text?: string,
): 'hardware_list_csv' | 'timber_list_csv' {
  if (text && isCadworkSteelCsv(text)) return 'hardware_list_csv';
  if (filenameLooksLikeHardwareList(file.name)) return 'hardware_list_csv';
  return 'timber_list_csv';
}

export function hardwareGroupFromCsvGroup(raw: string): HardwareListGroup {
  const g = raw.trim().toLowerCase();
  if (g === 'steel') return 'steel';
  if (g.startsWith('fastener')) return 'fasteners';
  if (g.startsWith('peg')) return 'pegs';
  return 'other';
}

export function storeyTokens(storey: string | undefined): string[] {
  return (storey || '')
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function uniqueStoreyTokens(items: Array<{ storey?: string }>): string[] {
  const set = new Set<string>();
  for (const item of items) {
    for (const token of storeyTokens(item.storey)) set.add(token);
  }
  return Array.from(set).sort();
}

function parseCsvLine(line: string): string[] {
  // Cadwork puts inch marks (`3 1/2"`) inside unquoted fields. Only honor RFC
  // quotes when a field actually starts with `"`.
  const hasQuotedField = /(^|,)"/.test(line);
  if (!hasQuotedField) {
    return line.split(',').map((s) => s.trim());
  }
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function parseQty(raw: string): number {
  const n = Number(String(raw).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

function isUnitsRow(cells: string[]): boolean {
  return cells.some((c) => /\[(inch|lb|mm|kg)\]/i.test(c));
}

function isHeaderRow(cells: string[]): boolean {
  const lower = cells.map((c) => c.trim().toLowerCase());
  return lower.includes('storey') && lower.includes('name') && lower.some((h) => h.includes('weight'));
}

function isMetadataRow(cells: string[]): boolean {
  const first = (cells[0] || '').trim().toLowerCase();
  return (
    first.startsWith('3d file') ||
    first.startsWith('export date') ||
    first === 'date' ||
    first.startsWith('project name') ||
    first.startsWith('project number') ||
    first.startsWith('project part')
  );
}

export function parseHardwareCsv(csvText: string): HardwareCsvParseResult {
  const lines = csvText.replace(/^\uFEFF/, '').split(/\r?\n/);
  const errors: string[] = [];
  const items: ParsedHardwareCsvItem[] = [];

  let header: string[] | null = null;
  const idx = (name: string, aliases: string[] = []) => {
    if (!header) return -1;
    const lower = header.map((h) => h.trim().toLowerCase());
    const names = [name, ...aliases];
    for (const n of names) {
      const i = lower.findIndex((h) => h === n || h.startsWith(n));
      if (i >= 0) return i;
    }
    return -1;
  };

  for (const line of lines) {
    if (!line.trim()) continue;
    const cells = parseCsvLine(line).map((c) => c.trim());
    if (!header) {
      if (isHeaderRow(cells)) header = cells;
      continue;
    }
    if (isUnitsRow(cells) || isMetadataRow(cells) || isHeaderRow(cells)) continue;
    if (!cells.some((c) => c)) continue;

    const qty = parseQty(cells[idx('quantity')] || '');
    const name = cells[idx('name')] || '';
    if (!name && qty === 0) continue;

    const csvGroup = cells[idx('group')] || '';
    const storey = cells[idx('storey', ['story', 'level'])] || '';
    items.push({
      storey,
      productionNumber: cells[idx('no. production', ['no production', 'production'])] || '',
      csvGroup,
      group: hardwareGroupFromCsvGroup(csvGroup),
      subGroup: cells[idx('sub group', ['subgroup', 'sub-group'])] || '',
      name: name || csvGroup || 'Part',
      material: cells[idx('material')] || '',
      section: cells[idx('x-section (w/h) real', ['x-section', 'xsection'])] || '',
      length: cells[idx('length real')] || '',
      lengthRough: cells[idx('length rough')] || '',
      quantity: qty,
      weightLb: parseQty(cells[idx('weight real', ['weight'])] || ''),
    });
  }

  if (!header) errors.push('No hardware list header row found (expected Storey / Name / Weight).');
  if (header && items.length === 0) errors.push('No data rows found in hardware CSV.');

  return { items, storeys: uniqueStoreyTokens(items), errors };
}

export function newHardwareListItemId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `hw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyHardwareListItem(group: HardwareListGroup): HardwareListItem {
  return {
    id: newHardwareListItemId(),
    group,
    name: '',
    material: '',
    section: '',
    length: '',
    quantity: 1,
    weightLb: 0,
    unitCost: 0,
    total: 0,
  };
}

export function parsedHardwareToListItem(
  item: ParsedHardwareCsvItem,
  sourceProjectFileId?: string,
): HardwareListItem {
  return {
    id: newHardwareListItemId(),
    group: item.group,
    name: item.name,
    subGroup: item.subGroup || undefined,
    material: item.material,
    section: item.section,
    length: item.length,
    lengthRough: item.lengthRough || undefined,
    quantity: item.quantity,
    weightLb: item.weightLb,
    unitCost: 0,
    total: 0,
    storey: item.storey || undefined,
    sourceProductionNumber: item.productionNumber || undefined,
    sourceProjectFileId,
  };
}

export function hardwareGroupTotal(list: HardwareListItem[] | undefined, group: HardwareListGroup): number {
  return (list || []).filter((r) => r.group === group).reduce((sum, r) => sum + (Number(r.total) || 0), 0);
}

export function hardwareListTotal(list: HardwareListItem[] | undefined): number {
  return (list || []).reduce((sum, r) => sum + (Number(r.total) || 0), 0);
}

export function withHardwareLineTotal(item: HardwareListItem): HardwareListItem {
  const quantity = Number(item.quantity) || 0;
  const unitCost = Number(item.unitCost) || 0;
  return { ...item, total: Math.round(quantity * unitCost * 100) / 100 };
}

/**
 * Peg and fastener rows: the Cost field is the price each.
 * Line total is quantity × cost. A blank quantity counts as 1 once a cost is entered.
 */
export function applyHardwareQtyCost(
  row: HardwareListItem,
  patch: { quantity?: number; unitCost?: number },
): HardwareListItem {
  const quantityInput = patch.quantity !== undefined ? patch.quantity : Number(row.quantity) || 0;
  const unitCost = patch.unitCost !== undefined ? patch.unitCost : Number(row.unitCost) || 0;
  const quantity = quantityInput > 0 ? quantityInput : unitCost > 0 ? 1 : 0;
  return withHardwareLineTotal({ ...row, quantity, unitCost });
}

const LUMP_PACKAGE_NAME: Record<'pegs' | 'fasteners', string> = {
  pegs: 'Peg package',
  fasteners: 'Fastener package',
};

/** Replace pegs or fasteners rows with a single package line (or remove them at $0). */
export function setHardwareGroupLumpSum(
  list: HardwareListItem[] | undefined,
  group: 'pegs' | 'fasteners',
  amount: number,
): HardwareListItem[] {
  const items = list || [];
  const rest = items.filter((row) => row.group !== group);
  const value = Number.isFinite(amount) ? Math.max(0, amount) : 0;
  if (value <= 0) return rest;
  const existing = items.find((row) => row.group === group);
  const base = existing
    ? { ...existing, group, name: LUMP_PACKAGE_NAME[group], quantity: 1, unitCost: value }
    : { ...emptyHardwareListItem(group), name: LUMP_PACKAGE_NAME[group], unitCost: value };
  return [...rest, withHardwareLineTotal(base)];
}

/** Seed pegs / fasteners lump sums as list rows so existing proposals keep their dollars. */
export function seedHardwareListIfNeeded(materials: MaterialsNodeData): HardwareListItem[] {
  const existing = materials.hardwareList;
  if (Array.isArray(existing) && existing.length > 0) return existing;
  const rows: HardwareListItem[] = [];
  if ((materials.pegsCost || 0) > 0) {
    rows.push({
      ...emptyHardwareListItem('pegs'),
      id: 'seed-pegs',
      name: 'Peg package',
      quantity: 1,
      unitCost: materials.pegsCost,
      total: materials.pegsCost,
    });
  }
  if ((materials.fastenerPackageCost || 0) > 0) {
    rows.push({
      ...emptyHardwareListItem('fasteners'),
      id: 'seed-fasteners',
      name: 'Fastener package',
      quantity: 1,
      unitCost: materials.fastenerPackageCost,
      total: materials.fastenerPackageCost,
    });
  }
  if (rows.length > 0) return rows;
  return existing ?? [];
}

export function syncHardwareRollups(materials: MaterialsNodeData): MaterialsNodeData {
  const hardwareList = seedHardwareListIfNeeded(materials);
  const pegsCost = hardwareGroupTotal(hardwareList, 'pegs');
  const fastenerPackageCost = hardwareGroupTotal(hardwareList, 'fasteners');
  const timberTotal =
    materials.timberTotal ||
    (materials.timberList || []).reduce((sum, item) => sum + (item.total || 0), 0);
  const postBasesTotal = Array.isArray(materials.postBasesItems)
    ? materials.postBasesItems.reduce((sum, item) => sum + postBasesItemTotal(item), 0)
    : materials.postBasesTotal || (materials.postBasesQty || 0) * (materials.postBasesUnitCost || 0);
  const deckingTotal = materials.deckingTotal || 0;
  return {
    ...materials,
    hardwareList,
    pegsCost,
    fastenerPackageCost,
    timberTotal,
    postBasesTotal,
    total: timberTotal + hardwareListTotal(hardwareList) + postBasesTotal + deckingTotal,
  };
}

export function defaultStoreyStructureMap(
  storeys: string[],
  nodeSets: Array<{ id: string }>,
): Record<string, string> {
  const firstId = nodeSets[0]?.id || '';
  const map: Record<string, string> = {};
  for (const storey of storeys) map[storey] = firstId;
  return map;
}

/**
 * Copy parsed rows onto mapped structures. Replaces existing steel rows; leaves
 * fasteners / pegs (and other) rows in place.
 */
export function applyHardwareItemsToNodeSets(
  nodeSets: ProposalNodeSet[],
  parsed: ParsedHardwareCsvItem[],
  storeyToNodeSetId: Record<string, string>,
  sourceProjectFileId?: string,
): ProposalNodeSet[] {
  const mappedIds = new Set(Object.values(storeyToNodeSetId).filter(Boolean));
  const fallbackId = nodeSets[0]?.id;
  const itemsByNodeSet = new Map<string, HardwareListItem[]>();

  for (const raw of parsed) {
    const tokens = storeyTokens(raw.storey);
    const targetIds = new Set<string>();
    if (tokens.length === 0) {
      mappedIds.forEach((id) => targetIds.add(id));
      if (targetIds.size === 0 && fallbackId) targetIds.add(fallbackId);
    } else {
      for (const token of tokens) {
        const id = storeyToNodeSetId[token];
        if (id) targetIds.add(id);
      }
    }
    Array.from(targetIds).forEach((id) => {
      const list = itemsByNodeSet.get(id) || [];
      list.push(parsedHardwareToListItem(raw, sourceProjectFileId));
      itemsByNodeSet.set(id, list);
    });
  }

  return nodeSets.map((ns) => {
    const incoming = itemsByNodeSet.get(ns.id);
    if (!incoming) return ns;
    const seeded = seedHardwareListIfNeeded(ns.materials);
    const kept = seeded.filter((row) => row.group !== 'steel');
    const materials = syncHardwareRollups({
      ...ns.materials,
      hardwareList: [...kept, ...incoming],
    });
    return { ...ns, materials };
  });
}
